import React, { useRef, useState, useMemo, useEffect } from "react";
//import { Rnd } from "react-rnd";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
//import Mustache from "mustache";

/**
 * Enhanced Bill PDF Generator supporting:
 * - Multi-page tables (auto-pagination)
 * - Custom font embedding for receipts/branding
 * - Print-perfect mm sizing and DPI presets (A4 / Letter / Thermal 80mm)
 */

// --- Utility: mm to pt (1 mm = 2.83465 pt) ---
const mmToPt = (mm) => mm * 2.83465;

const PAGE_PRESETS = {
  A4: { width: mmToPt(210), height: mmToPt(297) },
  Letter: { width: mmToPt(216), height: mmToPt(279) },
  Thermal80: { width: mmToPt(80), height: mmToPt(200) }, // variable height
};

// --- Mock data ---
const MOCK_DATA = {
  billNo: "Bill-10O1134",
  finalAmt: 121.0,
  mapItemList: Array.from({ length: 65 }).map((_, i) => ({
    name: `Item ${i + 1}`,
    quantity: 1,
    price: 100,
    finalprice: 100,
  })),
};

export default function BillPdfWithPagination() {
  const [pageType, setPageType] = useState("A4");
  const [fontFamily, setFontFamily] = useState("Roboto");
  const [dataJson, setDataJson] = useState(JSON.stringify(MOCK_DATA, null, 2));
  const surfaceRef = useRef(null);

  const data = useMemo(() => {
    try {
      return JSON.parse(dataJson);
    } catch {
      return {};
    }
  }, [dataJson]);

  // --- Font embedding ---
  useEffect(() => {
    const fontLink = document.getElementById("custom-font");
    if (fontLink) fontLink.remove();
    const link = document.createElement("link");
    link.id = "custom-font";
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
      /\s/g,
      "+"
    )}:wght@400;700&display=swap`;
    document.head.appendChild(link);
  }, [fontFamily]);

  const renderTablePages = (items, maxRows = 25) => {
    const pages = [];
    for (let i = 0; i < items.length; i += maxRows) {
      const slice = items.slice(i, i + maxRows);
      pages.push(slice);
    }
    return pages;
  };

  const handleGeneratePDF = async () => {
    const { width, height } = PAGE_PRESETS[pageType];
    const doc = new jsPDF({ unit: "pt", format: [width, height] });

    const items = data.mapItemList || [];
    const pages = renderTablePages(items, 25);

    for (let i = 0; i < pages.length; i++) {
      if (i > 0) doc.addPage([width, height]);
      const html = document.createElement("div");
      html.innerHTML = generatePageHtml(data, pages[i], fontFamily);
      document.body.appendChild(html);
      const canvas = await html2canvas(html, { scale: 2 });
      const img = canvas.toDataURL("image/png");
      doc.addImage(img, "PNG", 0, 0, width, height);
      document.body.removeChild(html);
    }

    doc.save(`${data.billNo || "bill"}.pdf`);
  };

  return (
    <div className="p-6 font-sans min-h-screen bg-gray-50 text-gray-800" style={{ fontFamily }}>
      <h1 className="text-xl font-bold mb-4">Bill PDF Generator (Multi-page, Fonts, mm-perfect)</h1>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-semibold mb-1">Page Type</label>
          <select
            value={pageType}
            onChange={(e) => setPageType(e.target.value)}
            className="border rounded p-2 w-full"
          >
            {Object.keys(PAGE_PRESETS).map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1">Font Family</label>
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            className="border rounded p-2 w-full"
          >
            {['Roboto', 'Poppins', 'Open Sans', 'Inconsolata', 'Noto Sans'].map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={handleGeneratePDF}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg w-full"
          >Generate PDF</button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-1">Bill JSON</label>
        <textarea
          value={dataJson}
          onChange={(e) => setDataJson(e.target.value)}
          className="w-full h-56 font-mono text-xs border rounded-lg p-2"
        />
      </div>
    </div>
  );
}

function generatePageHtml(data, items, fontFamily) {
  const tableRows = items
    .map(
      (r, i) =>
        `<tr><td>${i + 1}</td><td>${r.name}</td><td style='text-align:right;'>${r.quantity}</td><td style='text-align:right;'>${r.price}</td><td style='text-align:right;'>${r.finalprice}</td></tr>`
    )
    .join("");

  return `
    <div style="width:100%; height:100%; font-family:'${fontFamily}', sans-serif; padding:20pt; box-sizing:border-box;">
      <h2 style="text-align:center; margin-bottom:12pt;">${data.billNo} — TAX INVOICE</h2>
      <table style="width:100%; border-collapse:collapse; font-size:10pt;">
        <thead>
          <tr style="border-bottom:1px solid #ccc;">
            <th>#</th><th>Item</th><th>Qty</th><th>Rate</th><th>Amt</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
      <p style="text-align:right; margin-top:16pt; font-weight:700;">Grand Total: ₹${data.finalAmt}</p>
      <p style="text-align:center; font-size:9pt; color:#666; margin-top:10pt;">Page auto-split. Custom font: ${fontFamily}</p>
    </div>`;
}
